import { isMoment } from 'moment'
import { isNonEmptyArray, validateString } from '..'
import { getTimezoneLessMoment } from '../momentjs'
import { EUROPEAN_DATE } from '../constants'
import { isInstitutionUser } from '../roles'

export const TERMINATED = -1
export const INACTIVE = 0
export const ACTIVE = 1

/**
 * Badge state of a student within an institution. Mirrors the back-end BadgeStates enum : the values are the ones
 * accepted by the `badge` filter of the students endpoint.
 */
export const BadgeStates = Object.freeze({
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  NONE: 'NONE'
})

export const BadgeStateLabels = Object.freeze({
  [BadgeStates.ACTIVE]: 'student.badge_state.active',
  [BadgeStates.INACTIVE]: 'student.badge_state.inactive',
  [BadgeStates.NONE]: 'student.badge_state.none'
})

/**
 * A badge belongs to an institution : only an institution user has badges to display and to filter on. The back-end
 * resolves them from his own institution, and has none to answer with for any other context.
 */
export const canSeeBadges = user => isInstitutionUser(user)

/**
 * Badge state of a student, from the assignations serialized by the `badge-assignations` context. The back-end only
 * serializes the assignations of the current institution that are valid today, so every one of them counts.
 * A student is ACTIVE as soon as he holds one active badge, INACTIVE when he only holds inactive ones.
 */
export const getStudentBadgeState = student => {
  if (!isNonEmptyArray(student?.badgeAssignations)) {
    return BadgeStates.NONE
  }

  const statuses = student.badgeAssignations.map(assignation => assignation.badge?.status)

  if (statuses.includes(ACTIVE)) {
    return BadgeStates.ACTIVE
  }

  return statuses.includes(INACTIVE) ? BadgeStates.INACTIVE : BadgeStates.NONE
}

export const Properties = Object.freeze({
  ID: 'id',
  BADGE_NUMBER: 'badgeNumber',
  RFID_NUMBER: 'rfidNumber',
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  STUDENT_ID: 'studentId',
  STUDENT_FIRST_NAME: 'studentFirstName',
  STUDENT_LAST_NAME: 'studentLastName',
  BADGE_ASSIGNATION_ID: 'badgeAssignationId'
})

export const getters = Object.freeze({
  id: badge => badge.id,
  badgeNumber: badge => badge.badgeNumber,
  rfidNumber: badge => badge.rfidNumber,
  startDate: badge => badge.startDate,
  endDate: badge => badge.endDate,
  stringStartDate: badge => isMoment(badge.startDate) ? badge.startDate.format(EUROPEAN_DATE) : null,
  stringEndDate: badge => isMoment(badge.endDate) ? badge.endDate.format(EUROPEAN_DATE) : null,
  student: badge => badge.studentId ? `${badge.studentFirstName} ${badge.studentLastName}` : null,
  studentId: badge => badge.studentId,
  studentFirstName: badge => badge.studentFirstName,
  studentLastName: badge => badge.studentLastName,
  badgeAssignationId: badge => badge.badgeAssignationId,
  status: badge => badge.status
})

export const Validators = Object.freeze([
  { key: Properties.BADGE_NUMBER, validator: i => validateString(getters.badgeNumber(i), { notEmpty: true }) },
  { key: Properties.START_DATE, validator: i => getters.startDate(i) !== null },
  { key: Properties.STUDENT_ID, validator: i => getters.studentId(i) !== null }
])

export class BadgeAssignation {
  constructor ({
    id = -1, badgeNumber = null, rfidNumber = null, startDate = null, endDate = null, studentId = null, studentFirstName = null, studentLastName = null,
    badgeAssignationId = null, status = ACTIVE
  }) {
    this.id = id
    this.badgeNumber = badgeNumber
    this.rfidNumber = rfidNumber
    this.startDate = getTimezoneLessMoment(startDate)
    this.endDate = getTimezoneLessMoment(endDate)
    this.studentId = studentId
    this.studentFirstName = studentFirstName
    this.studentLastName = studentLastName
    this.badgeAssignationId = badgeAssignationId
    this.status = status
  }
}
